import { Button, Input, Spinner } from '@sjuni/ui';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, ImageIcon, Upload, X } from 'lucide-react';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { FormError } from '@/components/FormError';
import { ApiRequestError, apiFetch } from '@/lib/api/client';
import type { ListResponse } from '@/lib/api/resource';
import type { MediaRow } from '@/routes/media/types';

/**
 * docs/05-ADMIN-DASHBOARD.md §4.5's media picker, and the client's two
 * requirements for it: an upload from here lands in the same library as
 * everything else, and an editor can pick a picture that is already there.
 *
 * It replaces a text box an editor typed a path into. That is worth saying
 * plainly, because it is the whole point: nobody could see what they had
 * chosen, nothing checked the file existed, and the same photograph was typed
 * out once per page that used it.
 *
 * The alt-text rule is enforced *here* as a courtesy and on the server as a
 * rule. An image with no Georgian description is shown, greyed, with the reason
 * — hiding it would leave an editor hunting for a photograph they know they
 * uploaded. `DescribedImage` is what actually refuses the save.
 */
export interface MediaPickerProps {
  label: string;
  hint?: string;
  /** The currently attached image, as the resource sends it. */
  value: MediaRow | null;
  /** Called with the new media id, or null when the image is removed. */
  onChange: (mediaId: number | null, media: MediaRow | null) => void;
  error?: string;
  disabled?: boolean;
}

export function MediaPicker({ label, hint, value, onChange, error, disabled }: MediaPickerProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  return (
    <div>
      <span className="mb-1.5 block text-body-sm font-semibold text-primary">{label}</span>
      {hint ? <span className="mb-1.5 block text-caption text-muted">{hint}</span> : null}

      <div className="flex flex-wrap items-start gap-3">
        {/*
          The picture, not its path. An editor who cannot see what is attached
          has to open the public site to find out.
        */}
        <span className="bg-surface-subtle grid size-24 shrink-0 place-items-center overflow-hidden rounded-card border border-subtle">
          {value ? (
            <img
              src={value.urls.thumb === '' ? value.urls.original : value.urls.thumb}
              alt={value.alt.ka ?? ''}
              className="size-full object-cover"
            />
          ) : (
            <ImageIcon size={20} strokeWidth={1.5} aria-hidden="true" className="text-muted" />
          )}
        </span>

        <span className="flex flex-wrap gap-2">
          <Button
            type="button"
            size="sm"
            variant="secondary"
            disabled={disabled}
            onClick={() => setOpen(true)}
          >
            {value ? t('media.picker.change') : t('media.picker.choose')}
          </Button>

          {value ? (
            <Button
              type="button"
              size="sm"
              variant="ghost"
              disabled={disabled}
              onClick={() => onChange(null, null)}
            >
              {t('media.picker.remove')}
            </Button>
          ) : null}
        </span>
      </div>

      {error ? <p className="text-danger-600 mt-1.5 text-caption">{error}</p> : null}

      {open ? (
        <MediaLibraryDialog
          onClose={() => setOpen(false)}
          onPick={(media) => {
            onChange(media.id, media);
            setOpen(false);
          }}
        />
      ) : null}
    </div>
  );
}

/**
 * The library, as a dialog: search, upload, pick.
 *
 * Exported because §4.2's toolbar needs the same dialog to insert an image into a
 * body, and a second implementation would be a second idea of what "the library"
 * shows — one of which would end up without the upload button, or without the
 * warning on an undescribed image.
 */
export function MediaLibraryDialog({
  onClose,
  onPick,
}: {
  onClose: () => void;
  onPick: (media: MediaRow) => void;
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const input = useRef<HTMLInputElement>(null);

  const [search, setSearch] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const images = useQuery({
    queryKey: ['admin', 'media', 'picker', search],
    queryFn: () =>
      apiFetch<ListResponse<MediaRow>>('/admin/media', {
        query: { search: search.trim() === '' ? undefined : search.trim(), per_page: 36 },
      }),
  });

  /**
   * The client's "upload from any content" half.
   *
   * The same endpoint the library screen uses, so the picture is in the library
   * the moment it exists — and picked straight away, because the editor came
   * here to attach one rather than to file it.
   */
  const upload = async (files: FileList | null) => {
    const file = files?.[0];

    if (!file) {
      return;
    }

    setError(null);
    setBusy(true);

    const body = new FormData();

    body.append('file', file);

    try {
      const created = await apiFetch<{ data: MediaRow }>('/admin/media', {
        method: 'POST',
        body,
      });

      void queryClient.invalidateQueries({ queryKey: ['admin', 'media'] });

      /*
       * A fresh upload has no description yet, so it cannot be attached. Rather
       * than pick it and let the save fail, the grid refreshes and the reason
       * is shown against it — the editor adds the alt text in Media and comes
       * back. Picking it silently would produce a 422 on a field they cannot
       * see from here.
       */
      if (created.data.has_alt) {
        onPick(created.data);
      } else {
        setError(t('media.picker.uploadedNeedsAlt'));
      }
    } catch (cause) {
      setError(cause instanceof ApiRequestError ? cause.message : t('errors.generic'));
    }

    setBusy(false);

    if (input.current) {
      input.current.value = '';
    }
  };

  const rows = images.data?.data ?? [];

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="picker-heading"
      /*
       * `rgba(26,24,20,…)`, not `bg-ink/40`.
       *
       * `--color-ink` is a CSS variable holding a hex, so Tailwind emits nothing
       * at all for the opacity modifier — the dialog had no scrim, and the page
       * behind it stayed fully legible and looked clickable. `tailwind-preset`'s
       * own test says exactly this and was failing on it; the value matches the
       * design system's `Modal`, which is what this dialog sits beside.
       */
      className="fixed inset-0 z-50 grid place-items-center bg-[rgba(26,24,20,0.55)] p-4"
    >
      <div className="flex max-h-[80vh] w-full max-w-3xl flex-col overflow-hidden rounded-panel bg-surface shadow-xl">
        <div className="flex items-start justify-between gap-3 border-b border-subtle p-5">
          <h2 id="picker-heading" className="text-h4 font-extrabold text-primary">
            {t('media.picker.title')}
          </h2>

          <Button
            type="button"
            size="sm"
            variant="ghost"
            aria-label={t('common.close')}
            onClick={onClose}
          >
            <X size={16} strokeWidth={1.75} aria-hidden="true" />
          </Button>
        </div>

        <div className="flex flex-wrap items-end gap-3 border-b border-subtle p-5">
          <Input
            label={t('list.search')}
            hideLabel
            placeholder={t('media.searchPlaceholder')}
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />

          <input
            ref={input}
            type="file"
            aria-label={t('media.chooseFiles')}
            accept="image/jpeg,image/png,image/gif,image/webp"
            className="sr-only"
            onChange={(event) => void upload(event.target.files)}
          />

          <Button
            type="button"
            size="sm"
            disabled={busy}
            iconLeft={<Upload size={16} strokeWidth={1.75} aria-hidden="true" />}
            onClick={() => input.current?.click()}
          >
            {busy ? t('media.picker.uploading') : t('media.picker.upload')}
          </Button>
        </div>

        <div className="overflow-y-auto p-5">
          <FormError message={error} />

          {images.isPending ? <Spinner size="lg" label={t('common.loading')} /> : null}

          {!images.isPending && rows.length === 0 ? (
            <p className="text-body-sm text-secondary">{t('media.picker.empty')}</p>
          ) : null}

          <ul className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-5">
            {rows.map((row) => (
              <li key={row.id} className="h-full">
                <button
                  type="button"
                  // Shown but refused. Hiding it would leave an editor hunting
                  // for a photograph they know they uploaded.
                  disabled={!row.has_alt}
                  onClick={() => onPick(row)}
                  className="group flex h-full w-full flex-col overflow-hidden rounded-card border border-subtle text-start focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-navy-600 disabled:opacity-50"
                >
                  <span className="bg-surface-subtle block aspect-[4/3] overflow-hidden">
                    <img
                      src={row.urls.thumb === '' ? row.urls.original : row.urls.thumb}
                      alt={row.alt.ka ?? ''}
                      loading="lazy"
                      className="size-full object-cover"
                    />
                  </span>

                  <span className="block p-2">
                    <span className="block truncate text-caption font-semibold text-primary">
                      {row.name}
                    </span>

                    {row.has_alt ? null : (
                      <span className="mt-1 inline-flex items-center gap-1 text-caption text-gold-700">
                        <AlertTriangle size={12} strokeWidth={1.75} aria-hidden="true" />
                        {t('media.noAltShort')}
                      </span>
                    )}
                  </span>
                </button>
              </li>
            ))}
          </ul>

          {rows.some((row) => !row.has_alt) ? (
            <p className="mt-4 flex items-start gap-2 text-caption text-muted">
              <AlertTriangle
                size={14}
                strokeWidth={1.75}
                aria-hidden="true"
                className="mt-0.5 shrink-0"
              />
              {t('media.picker.greyedExplainer')}
            </p>
          ) : null}
        </div>
      </div>
    </div>
  );
}
